import { Avatar, Box, Icon, Spinner, Table, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import usePagination from 'modules/core/hooks/usePagination'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'
import { employeeUser } from 'modules/core/store/collections/users'

import type Employee from 'modules/employees/store/models/Employee'
import { useSearchEmployees } from 'modules/payroll_bundle/hooks/useSearchEmployees'

import TableOptions from './TableOptions'

interface Props {
  employees: Employee[]
  loading: boolean
}

const EMPLOYEES_PER_PAGE = 50

const EmployeesTable = ({ employees, loading }: Props) => {
  const { navigation } = useNavigation()

  const {
    paginatedItems: paginatedEmployees,
    loadMore,
    hasMore,
    page,
  } = usePagination({
    batchSize: EMPLOYEES_PER_PAGE,
    items: employees.sort((a, b) =>
      employeeUser(a).fullName().localeCompare(employeeUser(b).fullName())
    ),
  })
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()
  const filteredEmployees = useSearchEmployees(debouncedSearch, paginatedEmployees).get()
  const isEmpty = filteredEmployees.length === 0

  return (
    <Table options={() => <TableOptions search={search} onSearch={onSearch} disabled={loading} />}>
      <Table.BodyWithInfiniteScroll page={page} loadMore={loadMore} hasMore={hasMore} colSpan={1}>
        {loading && (
          <Table.Row>
            <Table.Cell>
              <Box alignItems="center">
                <Spinner />
              </Box>
            </Table.Cell>
          </Table.Row>
        )}

        {isEmpty && !loading && (
          <Table.Blank
            colSpan={1}
            text={i18n.t(
              'app_store_v2.apps.payroll_bundle.settings.payroll_policy.employees.empty_label'
            )}
          />
        )}

        {!isEmpty &&
          !loading &&
          filteredEmployees.map((employee) => (
            <Table.Row.Router
              key={employee.id}
              to={navigation.employees.id.fullPath({ id: employee.id })}
            >
              <Table.Cell>
                <Box flexDirection="row" gap="s12" alignItems="center">
                  <Avatar
                    size="s40"
                    url={buildUrl(employeeUser(employee).avatar)}
                    label={employeeUser(employee).fullName()}
                  />
                  <Text.Normal color="grey1000">{employeeUser(employee).fullName()}</Text.Normal>
                </Box>
              </Table.Cell>
              <Table.Cell colSpan={1}>
                <Box flexDirection="row" justifyContent="flexEnd">
                  <Icon.Medium icon="arrow-right" />
                </Box>
              </Table.Cell>
            </Table.Row.Router>
          ))}
      </Table.BodyWithInfiniteScroll>
    </Table>
  )
}

export default observer(EmployeesTable)
